import { useId, useRef } from "react";
import { Button, Icon, Typography } from "@/deps/ui-library";

/**
 * Files to send with a ticket or reply. What the platform accepts — types, sizes, how many — is its
 * to decide and to say when it refuses; nothing is second-guessed here.
 *
 * The file input itself is hidden and opened by an RDS button: a native file control cannot be
 * styled, and RDS's upload input validates types and sizes, which is the platform's call here.
 */
export function FilePicker({
  files,
  onChange,
}: {
  files: File[];
  onChange: (files: File[]) => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col items-start gap-1">
      <Typography.Text id={`${id}-label`} as="span" variant="small" weight="medium">
        Attachments
      </Typography.Text>
      <input
        ref={input}
        id={id}
        type="file"
        multiple
        hidden
        aria-labelledby={`${id}-label`}
        onChange={(event) => {
          onChange([...files, ...Array.from(event.target.files ?? [])]);
          event.target.value = "";
        }}
      />
      <Button
        colorScheme="default"
        variant="outline"
        size="sm"
        leftIcon="Paperclip"
        aria-describedby={`${id}-label`}
        onClick={() => input.current?.click()}
      >
        Add files
      </Button>
      {files.length > 0 ? (
        <ul className="flex w-full flex-col gap-1">
          {files.map((file) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="text-foreground flex items-center justify-between gap-2 text-xs"
            >
              <span className="flex min-w-0 items-center gap-1">
                <Icon name="File" size="xs" />
                <span className="truncate">{file.name}</span>
              </span>
              <Button
                colorScheme="default"
                variant="ghost"
                size="xs"
                aria-label={`Remove ${file.name}`}
                onClick={() => onChange(files.filter((other) => other !== file))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
